/**
 * Multi-window outlook panel rendered when a Market row is expanded.
 *
 * Fetches `/api/market/{id}/outlook` and shows projected points for
 * GW windows 1, 3, 5, 10 with a short explanation of the derived
 * recommendation tag (SHORT_TERM_FAVOURITE · SLOW_BURNER · CONSISTENT).
 */
import React, { useEffect, useState } from "react";
import { Warning, Hourglass, Seal } from "@phosphor-icons/react";
import { getMarketOutlook } from "../lib/api";

const TAG_META = {
    SHORT_TERM_FAVOURITE: {
        label: "Short-term favourite",
        color: "#FFD180",
        icon: Warning,
        copy: "Strong GW1 but softens by GW10 — consider short-term punt and rotation later.",
    },
    SLOW_BURNER: {
        label: "Slow burner",
        color: "#9DB7F5",
        icon: Hourglass,
        copy: "Soft GW1, better GW5+ — buy now to ride the improving fixture run.",
    },
    CONSISTENT: {
        label: "Consistent",
        color: "#9EE8B4",
        icon: Seal,
        copy: "Stable across all windows — low-variance captain / set-and-forget pick.",
    },
    INSUFFICIENT_DATA: {
        label: "Limited data",
        color: "#A1A1AA",
        icon: Warning,
        copy: "Prediction pool didn't include this player across all four windows.",
    },
};

export default function OutlookPanel({ playerId }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setError(null);
        setData(null);
        getMarketOutlook(playerId)
            .then((d) => {
                if (!cancelled) setData(d);
            })
            .catch(() => {
                if (!cancelled) setError("Outlook unavailable");
            });
        return () => { cancelled = true; };
    }, [playerId]);

    if (error) {
        return (
            <div className="font-mono text-xs text-fa-red/70" data-testid={`outlook-error-${playerId}`}>
                {error}
            </div>
        );
    }
    if (!data) {
        return (
            <div className="font-mono text-xs text-white/40 italic" data-testid={`outlook-loading-${playerId}`}>
                Loading outlook…
            </div>
        );
    }

    const meta = TAG_META[data.recommendation_tag] || TAG_META.INSUFFICIENT_DATA;
    const Icon = meta.icon;

    return (
        <div className="space-y-3" data-testid={`outlook-panel-${playerId}`}>
            <div
                className="flex items-start gap-2 border-l-2 px-3 py-2 bg-black/30"
                style={{ borderColor: meta.color }}
            >
                <Icon size={18} weight="fill" className="mt-0.5 flex-shrink-0" color={meta.color} />
                <div>
                    <div
                        className="font-mono text-[10px] uppercase tracking-widest"
                        style={{ color: meta.color }}
                        data-testid={`outlook-tag-${playerId}`}
                    >
                        {meta.label}
                        {data.delta_short_vs_long_pct != null && (
                            <span className="ml-2 text-white/40">
                                GW1 vs long-term: {data.delta_short_vs_long_pct > 0 ? "+" : ""}
                                {data.delta_short_vs_long_pct}%
                            </span>
                        )}
                    </div>
                    <div className="font-mono text-[11px] text-white/70 mt-1">{meta.copy}</div>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid={`outlook-windows-${playerId}`}>
                {data.windows.map((w) => (
                    <WindowCard key={w.window} w={w} />
                ))}
            </div>
        </div>
    );
}

function WindowCard({ w }) {
    const riskColor = {
        LOW: "#9EE8B4",
        MEDIUM: "#FFD180",
        HIGH: "#FF6B6B",
    }[w.risk_level] || "#888";
    return (
        <div className="border border-white/10 bg-black/30 p-3" data-testid={`outlook-gw${w.window}`}>
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">
                GW window {w.window}
            </div>
            <div className="font-display font-black text-2xl tabular-nums text-fa-yellow mt-1">
                {w.projected_points != null ? w.projected_points.toFixed(1) : "—"}
            </div>
            <div className="font-mono text-[10px] text-white/50 tabular-nums">
                {w.per_fixture != null ? `${w.per_fixture.toFixed(2)} / fx` : "—"}
            </div>
            {w.risk_level && (
                <div
                    className="font-mono text-[9px] uppercase tracking-widest mt-2"
                    style={{ color: riskColor }}
                >
                    {w.risk_level} risk · {w.confidence}
                </div>
            )}
            {w.next_fixtures && w.next_fixtures.length > 0 && (
                <div className="flex items-center gap-1 mt-2 flex-wrap">
                    {w.next_fixtures.slice(0, 5).map((f, i) => (
                        <span
                            key={`${f.gw || i}-${f.opponent}`}
                            className="font-mono text-[9px] tabular-nums px-1 py-0.5 bg-white/5"
                            title={`GW${f.gw} ${f.home ? "vs" : "@"} ${f.opponent} FDR ${f.fdr}`}
                        >
                            {f.opponent}
                            <span className="text-white/40">·{f.fdr}</span>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}
